import { SCHOOL_PORTAL_ROUTE } from 'config'
import { selectors as globalSelectors } from 'global-reducer'
import { removeSpecificInterestType } from 'utils'

export const SECTION_KEYS = {
  OVERVIEW: 'overview',
  PARTNERSHIPS_INTERESTS: 'partnerships_with_interests',
}

export const SECTION_META = {
  [SECTION_KEYS.OVERVIEW]: {
    label: 'Overview',
    tooltip: 'All fields are required to complete this section',
    route: SCHOOL_PORTAL_ROUTE + '/summary/overview',
  },
  [SECTION_KEYS.PARTNERSHIPS_INTERESTS]: {
    label: 'School Partnerships & Interests',
    tooltip:
      'To complete this section, indicate whether you are currently looking for partnerships. If you select “Yes,” you must also complete the Interests section. You do not need to add partnerships to complete this section.',
    route: SCHOOL_PORTAL_ROUTE + '/summary/partnerships-with-interests',
  },
}

export function getSections() {
  return [SECTION_KEYS.OVERVIEW, SECTION_KEYS.PARTNERSHIPS_INTERESTS]
}

export function computePercent(completedCount, totalCount) {
  if (!totalCount) return 0
  if (completedCount <= 0) return 0
  return Math.round((completedCount / totalCount) * 100)
}

function hasArtsLiaison(school) {
  const liaison = school && school.artsLiaison
  return Boolean(
    liaison && liaison.firstName && liaison.lastName && liaison.email
  )
}

function hasAccessiblePrograms(school) {
  return (
    Array.isArray(school && school.accessiblePrograms) &&
    school.accessiblePrograms.length > 0
  )
}

function isNonEmptyArray(value) {
  return Array.isArray(value) && value.length > 0
}

// Legacy fallback: section counts as complete if *any* interest is provided.
// Used only when category-availability info isn't supplied to the bar model.
function hasInterestResponses(school) {
  const requestedSpecificInterest =
    school &&
    school.requestedSpecificInterest &&
    school.requestedSpecificInterest.otherText

  return Boolean(
    isNonEmptyArray(school && school.requestedResources) ||
      isNonEmptyArray(school && school.requestedProgramTypes) ||
      isNonEmptyArray(school && school.requestedLanguages) ||
      isNonEmptyArray(school && school.requestedOutcomeTypes) ||
      isNonEmptyArray(school && school.requestedPrograms) ||
      requestedSpecificInterest
  )
}

// When a school is looking for partnerships, every interest category that is
// available to them must have a response — except the free-text "School's
// Interests" box, since they may not have specifics. Categories that don't
// apply to the community (e.g. languages/outcomes) are not required.
function hasAllRequiredInterestResponses(school, options = {}) {
  const available = options.interestCategoriesAvailable
  if (!available) return hasInterestResponses(school)

  const requirements = []
  if (available.artsResources)
    requirements.push(isNonEmptyArray(school && school.requestedResources))
  if (available.programTypes)
    requirements.push(isNonEmptyArray(school && school.requestedProgramTypes))
  if (available.languages)
    requirements.push(isNonEmptyArray(school && school.requestedLanguages))
  if (available.outcomes)
    requirements.push(isNonEmptyArray(school && school.requestedOutcomeTypes))
  if (available.disciplines)
    requirements.push(isNonEmptyArray(school && school.requestedPrograms))

  if (requirements.length === 0) return true

  return requirements.every(Boolean)
}

function isPartnershipsInterestsComplete(school, options = {}) {
  if (options.isPartnershipQuestionUnavailable) return true

  // Only "Yes" requires the interests to be filled in. An explicit "No"
  // (false) and an unanswered value (null/undefined) both complete the
  // section: the toggle renders a null value identically to "No" (off), so a
  // saved "No" must be honored even when the underlying value is still null.
  if (school.openToPartnerships === true) {
    return hasAllRequiredInterestResponses(school, options)
  }

  return true
}

// Determines which interest categories are available for the current community,
// so the completion logic only requires the categories a school can actually
// fill in. Build from global state and pass into `buildBarModel`.
export function buildInterestCompletionOptions(state) {
  if (!state) return {}

  const resourceTypes = globalSelectors.resourceTypes(state) || []
  const programTypes = globalSelectors.orderedProgramTypes(state) || []
  const languages = globalSelectors.languages(state) || []
  const outcomeTypes = globalSelectors.outcomeTypes(state) || []
  const disciplines = globalSelectors.definedDisciplines(state) || []

  return {
    interestCategoriesAvailable: {
      artsResources: removeSpecificInterestType(resourceTypes).length > 0,
      programTypes: programTypes.length > 0,
      languages: languages.length > 0,
      outcomes: outcomeTypes.length > 0,
      disciplines: disciplines.length > 0,
    },
  }
}

export function isSectionComplete(sectionKey, school, options = {}) {
  if (!school) return false

  switch (sectionKey) {
    case SECTION_KEYS.OVERVIEW:
      // The "physical space dedicated to the arts" question is intentionally
      // excluded from completion — it does not gate the Overview section.
      return hasArtsLiaison(school) && hasAccessiblePrograms(school)
    case SECTION_KEYS.PARTNERSHIPS_INTERESTS:
      return isPartnershipsInterestsComplete(school, options)
    default:
      return false
  }
}

export function deriveCompletedSections(school, options = {}) {
  return getSections().filter((sectionKey) =>
    isSectionComplete(sectionKey, school, options)
  )
}

export function buildBarModel(school, options = {}) {
  const sections = getSections()
  const completedSections = deriveCompletedSections(school, options)
  const completedSet = new Set(completedSections)
  const orderedCompletedSections = sections.filter((sectionKey) =>
    completedSet.has(sectionKey)
  )
  const orderedIncompleteSections = sections.filter(
    (sectionKey) => !completedSet.has(sectionKey)
  )
  const orderedSections = [
    ...orderedCompletedSections,
    ...orderedIncompleteSections,
  ]

  return {
    sections: orderedSections.map((key) => ({ key, ...SECTION_META[key] })),
    completedSections: orderedCompletedSections,
    percent: computePercent(completedSections.length, sections.length),
  }
}
